/**
 * Boot skeleton of the console (QA 2026-09-15 **UX-504**).
 *
 * The first sign-in of a session waits on a cold worker: 18.1 s measured end to end, sixteen of
 * them behind the single word "Cargando…" centred on an empty canvas — nothing said the
 * credentials had been accepted. The TV got a shaped skeleton for exactly this in iteration 2
 * (UX-110); this is the console's. It draws the shell the operator is about to get — the dark
 * sidebar, a page header, a toolbar and eight table rows — so the wait reads as "the console is
 * coming" instead of "nothing happened", and it says out loud when the wait is unusual.
 */
import { useEffect, useState } from 'react';

import { cn } from '../../lib/cn';

/** After this long the wait stops being normal and the skeleton says so. */
export const SLOW_BOOT_MS = 6000;

function Bar({ className }: { className?: string }) {
  return <span className={cn('block animate-pulse rounded bg-current opacity-10', className)} />;
}

export function BootSkeleton({ label, slowLabel }: { label?: string; slowLabel?: string }) {
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    if (!slowLabel) return;
    const timer = setTimeout(() => setSlow(true), SLOW_BOOT_MS);
    return () => clearTimeout(timer);
  }, [slowLabel]);

  return (
    <div
      className="flex h-full min-h-dvh bg-canvas"
      data-testid="boot-skeleton"
      aria-busy="true"
      role="status"
      aria-live="polite"
    >
      <div className="flex w-[216px] shrink-0 flex-col gap-2 bg-sidebar p-4 text-white">
        <Bar className="h-4 w-28" />
        <Bar className="mt-4 h-3 w-20" />
        {Array.from({ length: 6 }, (_, i) => (
          <Bar key={i} className="h-3 w-full" />
        ))}
        <Bar className="mt-auto h-9 w-full" />
      </div>

      <div className="min-w-0 flex-1 px-6 pt-5 text-ink">
        <Bar className="h-6 w-56" />
        <Bar className="mt-2 h-3 w-80" />
        <div className="mt-6 rounded-[12px] border border-line bg-panel p-4">
          <Bar className="h-4 w-40" />
          {Array.from({ length: 8 }, (_, i) => (
            <Bar key={i} className="mt-3 h-3 w-full" />
          ))}
        </div>
        {label ? <p className="mt-4 text-[13px] text-muted">{slow ? slowLabel : label}</p> : null}
      </div>
    </div>
  );
}
